// Modal de marcação manual de pagamento (Story 4.7).
// Trigger: linha de cobrança com status `enviada` ou `vencida` na listagem.

import * as Dialog from '@radix-ui/react-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  markPaidManualSchema,
  type ManualPaymentMethod,
  type MarkPaidManualRequest,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useMarkPaidManual } from '../../hooks/use-charges';
import { extractErrorMessage } from '../../lib/errors';
import { firstErrorMessage } from '../../lib/form-errors';

const METHOD_OPTIONS: { value: ManualPaymentMethod; label: string }[] = [
  { value: 'pix_manual', label: 'PIX direto (fora do link Asaas)' },
  { value: 'ted', label: 'TED / transferência' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'deposito', label: 'Depósito bancário' },
  { value: 'outro', label: 'Outro' },
];

interface MarkPaidManualModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  charge: { id: string; clientName: string; amount: string; description: string } | null;
}

function defaultPaidAt(): string {
  // <input type="datetime-local"> espera "YYYY-MM-DDTHH:mm" SEM timezone.
  const now = new Date();
  const tzOffsetMin = now.getTimezoneOffset();
  const local = new Date(now.getTime() - tzOffsetMin * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Schema do FORMULÁRIO — não é o mesmo da API, de propósito.
 *
 * BUG REAL 2026-08-31: o modal era IMPOSSÍVEL de enviar. O `<input
 * type="datetime-local">` produz "2026-08-31T14:54" (sem segundos, sem fuso),
 * mas `markPaidManualSchema.paidAt` é `.datetime()` — ISO 8601 completo. Como o
 * resolver valida o VALOR DO FORM, ele reprovava com "Data deve estar no formato
 * ISO 8601" e o `onSubmit` — que é justamente onde a conversão pra ISO acontece —
 * nunca rodava. Erro em loop, sem saída.
 *
 * A conversão pra ISO fica no submit; aqui o campo é validado no formato que o
 * browser realmente emite.
 */
const paidAtLocalSchema = z
  .string()
  .min(1, 'Informe a data do pagamento')
  .refine((v) => !Number.isNaN(new Date(v).getTime()), 'Data inválida');

/**
 * Campo OPCIONAL de texto: `<input>` não preenchido devolve "" — que NÃO é
 * `undefined`. Sem este preprocess, `.optional()` não protege nada: o validador
 * roda em cima da string vazia e reprova.
 *
 * BUG REAL 2026-08-31 (o segundo deste modal): deixar "URL do anexo" em branco
 * — o caso normal — derrubava o submit com "URL inválida", um erro sobre um
 * campo que o operador nunca tocou.
 */
function opcionalQuandoVazio<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((v) => (v === '' ? undefined : v), schema.optional());
}

const markPaidManualFormSchema = markPaidManualSchema.extend({
  paidAt: paidAtLocalSchema,
  attachmentUrl: opcionalQuandoVazio(
    z.string().url('URL inválida').max(1000, 'URL muito longa'),
  ),
  notes: opcionalQuandoVazio(
    z.string().trim().max(500, 'Observações devem ter no máximo 500 caracteres'),
  ),
});

export function MarkPaidManualModal({
  open,
  onOpenChange,
  charge,
}: MarkPaidManualModalProps): JSX.Element {
  const mutation = useMarkPaidManual();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MarkPaidManualRequest>({
    resolver: zodResolver(markPaidManualFormSchema),
    defaultValues: {
      amount: charge ? Number(charge.amount) : undefined,
      // datetime-local espera 'YYYY-MM-DDTHH:mm' sem TZ — ISO completo NÃO renderiza
      paidAt: defaultPaidAt(),
      method: 'pix_manual',
      externalReference: '',
    },
  });

  async function onSubmit(values: MarkPaidManualRequest): Promise<void> {
    if (!charge) return;
    try {
      // paidAt vem do <input type="datetime-local"> sem TZ. new Date(...) interpreta
      // no fuso do browser; .toISOString() converte pra UTC. Assume operador OOW
      // acessando do BR. Se vier de outro fuso, o ISO reflete o wall-clock daquele
      // fuso — não o BR. Sem fix porque uso é interno e doméstico.
      const paidAtIso = new Date(values.paidAt).toISOString();
      await mutation.mutateAsync({
        chargeId: charge.id,
        payload: { ...values, paidAt: paidAtIso },
      });
      toast.success(`Cobrança marcada como paga manualmente`, {
        description: 'Lembrete: Asaas continuará mostrando aguardando — conciliação manual mensal.',
      });
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao marcar como paga manual'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(560px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[12px] border border-line bg-panel p-6 shadow-2xl',
          )}
        >
          <div className="mb-5 flex items-center justify-between">
            <div>
              <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em]">
                Marcar paga manualmente
              </Dialog.Title>
              {charge && (
                <Dialog.Description className="mt-1 text-[12px] text-muted">
                  {charge.clientName} · {charge.description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-[8px] p-1.5 text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          {/* AC9 — aviso sobre divergência com Asaas */}
          <div className="mb-5 flex items-start gap-2 rounded-[8px] border border-warn/30 bg-warn/5 p-3">
            <AlertTriangle size={14} strokeWidth={1.8} className="mt-0.5 shrink-0 text-warn" />
            <p className="text-[12px] leading-[1.5] text-ink-2">
              <span className="font-medium">Atenção:</span> isso registra o pagamento{' '}
              <strong>fora do Asaas</strong>. A cobrança continuará aparecendo como{' '}
              <em>aguardando pagamento</em> no painel Asaas. Faça conciliação manual mensal.
            </p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit, (errs) =>
              toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
            )}
            className="flex flex-col gap-4"
          >
            <Field label="Forma de pagamento" required error={errors.method?.message}>
              {({ inputId }) => (
                <Select id={inputId} hasError={Boolean(errors.method)} {...register('method')}>
                  {METHOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Valor recebido (R$)"
              required
              error={errors.amount?.message}
              hint="Pode ser diferente do valor original (ex: cliente arredondou)"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="999999.99"
                  tabular
                  hasError={Boolean(errors.amount)}
                  {...register('amount', { valueAsNumber: true })}
                />
              )}
            </Field>

            <Field
              label="Data do pagamento"
              required
              error={errors.paidAt?.message}
              hint="Data real em que o cliente pagou (não a data atual)"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="datetime-local"
                  hasError={Boolean(errors.paidAt)}
                  {...register('paidAt')}
                />
              )}
            </Field>

            <Field
              label="Referência"
              required
              error={errors.externalReference?.message}
              hint="ID PIX, número da TED, código de comprovante (auditoria fiscal)"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="text"
                  maxLength={200}
                  placeholder="Ex: PIX_E2837492 / TED 9988"
                  hasError={Boolean(errors.externalReference)}
                  {...register('externalReference')}
                />
              )}
            </Field>

            <Field
              label="URL do anexo (opcional)"
              error={errors.attachmentUrl?.message}
              hint="Cole o link do comprovante no Drive / S3 / WhatsApp"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="url"
                  placeholder="https://..."
                  maxLength={1000}
                  hasError={Boolean(errors.attachmentUrl)}
                  {...register('attachmentUrl')}
                />
              )}
            </Field>

            <Field label="Observações (opcional)" error={errors.notes?.message}>
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={2}
                  maxLength={500}
                  placeholder="Contexto adicional pra auditoria"
                  hasError={Boolean(errors.notes)}
                  {...register('notes')}
                />
              )}
            </Field>

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={mutation.isPending}>
                {mutation.isPending ? 'Registrando…' : 'Confirmar pagamento'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
